import { Injectable, inject } from '@angular/core';
import {Observable, of, zip} from 'rxjs';
import {map, mergeMap} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import {environment} from '../../environments/environment';
import {MaandJaar} from '../core/date/maand-jaar';
import {Page, PageDto} from '../core/pagination/page';
import {ExtendedPrestatieCijfer, PrestatieCijfer, ExtendedPrestatieCijferDto, TaakCijfer} from './prestatie-cijfer';
import {PrestatieCijfersPageRequest} from './prestatie-cijfers-page-request';
import {DienstService} from '../dienst/dienst.service';
import {Diensten} from '../dienst/diensten';
import * as lodash from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class PrestatieCijfersService {
  private http = inject(HttpClient);
  private dienstService = inject(DienstService);

  downloadPrestatieCijfers$(maandJaar: MaandJaar): Observable<Array<PrestatieCijfer>> {
    const firstPageRequest = PrestatieCijfersPageRequest.firstPrestatieCijfersRequest(maandJaar);
    return this.getPrestatieCijfers(firstPageRequest, [])
      .pipe(
        map(extendedPrestatieCijfers => extendedPrestatieCijfers.map(extendedPrestatieCijfer => extendedPrestatieCijfer.toPrestatieCijfer())));
  }

  downloadTaakCijfers$(maandJaar: MaandJaar): Observable<Array<TaakCijfer>> {
    const firstPageRequest = PrestatieCijfersPageRequest.firstPrestatieCijfersRequest(maandJaar);

    return zip(
      this.dienstService.getDiensten$(),
      this.getPrestatieCijfers(firstPageRequest, [])
    )
      .pipe(
        map(([diensten, extendedPrestatieCijfers]: [Diensten, Array<ExtendedPrestatieCijfer>]) =>
          extendedPrestatieCijfers.flatMap(extendedPrestatieCijfer => extendedPrestatieCijfer.toTaakCijfers(diensten))),
        map(taakcijfers => lodash.sortBy(taakcijfers, ['clientnummer']))
      );
  }

  private getPrestatieCijfers(pageRequest: PrestatieCijfersPageRequest, items: Array<ExtendedPrestatieCijfer>): Observable<Array<ExtendedPrestatieCijfer>> {
    return this.getFirstPrestatieCijferPage(pageRequest).pipe(
      mergeMap((page: Page<ExtendedPrestatieCijfer>) => {
        items.push(...page.getItems());
        if (!page.isLastPage()) {
          return this.getPrestatieCijfers(PrestatieCijfersPageRequest.nextPageRequestFromPage(page, pageRequest.getMaandJaar()), items);
        }
        return of(items);
      })
    );
  }

  private getFirstPrestatieCijferPage(pageRequest: PrestatieCijfersPageRequest): Observable<Page<ExtendedPrestatieCijfer>> {
    return this.http.post<PageDto<ExtendedPrestatieCijferDto>>(`${environment.apiUrl}/export/prestatie/cijfers`, pageRequest)
      .pipe(map(pageDto => new Page<ExtendedPrestatieCijfer>(
          pageDto.ascending,
          pageDto.pageSize,
          pageDto.pageNumber,
          pageDto.lastPage,
          pageDto.items.map(dto => ExtendedPrestatieCijfer.deserialize(dto))
        ))
      );
  }
}
